<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

// What the customer will see on the phone: header media, body with the
// variables filled, footer and buttons — drawn from the approved template.
const props = defineProps({
  template: { type: Object, default: null },
  processedParams: { type: Object, default: () => ({}) },
  mediaPreviewUrl: { type: String, default: '' },
  senderName: { type: String, default: '' },
});

const { t } = useI18n();

const component = type =>
  (props.template?.components || []).find(
    c => String(c.type).toUpperCase() === type
  );

const header = computed(() => component('HEADER'));
const body = computed(() => component('BODY'));
const footer = computed(() => component('FOOTER'));
const buttons = computed(() => component('BUTTONS')?.buttons || []);

const headerFormat = computed(() =>
  String(header.value?.format || '').toUpperCase()
);

const fill = (text, values) =>
  String(text || '').replace(/{{\s*([^}]+?)\s*}}/g, (m, key) => {
    const k = String(key).trim();
    const v = values?.[k] ?? values?.[`{{${k}}}`];
    return v ? String(v) : m;
  });

const bodyText = computed(() =>
  fill(body.value?.text, props.processedParams?.body)
);
const headerText = computed(() =>
  headerFormat.value === 'TEXT'
    ? fill(header.value?.text, props.processedParams?.header)
    : ''
);

const now = computed(() =>
  new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
);
</script>

<template>
  <div
    class="mx-auto w-full max-w-[340px] rounded-[28px] border-[6px] border-n-slate-12/80 bg-[#efe7dd] p-2 shadow-lg dark:border-n-slate-7"
    dir="auto"
  >
    <div
      class="mb-2 flex items-center gap-2 rounded-t-[18px] bg-[#075e54] px-3 py-2 text-white"
    >
      <span
        class="flex size-8 items-center justify-center rounded-full bg-white/20 text-sm font-bold"
      >
        {{ (senderName || 'T').slice(0, 1) }}
      </span>
      <div class="min-w-0 leading-tight">
        <p class="truncate text-sm font-semibold">
          {{ senderName || t('CAMPAIGN.WHATSAPP.WIZARD.PREVIEW.SENDER') }}
        </p>
        <p class="text-[11px] opacity-80">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.PREVIEW.BUSINESS') }}
        </p>
      </div>
    </div>

    <div class="min-h-[220px] px-1 pb-2">
      <p v-if="!template" class="pt-16 text-center text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.PREVIEW.EMPTY') }}
      </p>
      <div
        v-else
        class="me-6 rounded-lg rounded-ss-none bg-white p-1.5 text-[13px] text-n-slate-12 shadow-sm dark:bg-n-solid-3"
      >
        <div
          v-if="['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerFormat)"
          class="mb-1.5 overflow-hidden rounded-md bg-n-slate-3"
        >
          <img
            v-if="headerFormat === 'IMAGE' && mediaPreviewUrl"
            :src="mediaPreviewUrl"
            class="max-h-44 w-full object-cover"
            alt=""
          />
          <div
            v-else
            class="flex h-28 flex-col items-center justify-center gap-1 text-xs text-n-slate-11"
          >
            <span
              :class="
                headerFormat === 'IMAGE'
                  ? 'i-lucide-image'
                  : headerFormat === 'VIDEO'
                    ? 'i-lucide-video'
                    : 'i-lucide-file-text'
              "
              class="size-6"
            />
            {{
              mediaPreviewUrl
                ? t('CAMPAIGN.WHATSAPP.WIZARD.PREVIEW.MEDIA_READY')
                : t('CAMPAIGN.WHATSAPP.WIZARD.PREVIEW.MEDIA_MISSING')
            }}
          </div>
        </div>
        <p v-if="headerText" class="mb-1 px-1 font-semibold">
          {{ headerText }}
        </p>
        <p class="whitespace-pre-wrap px-1 leading-relaxed">
          {{ bodyText }}
        </p>
        <p v-if="footer" class="mt-1 px-1 text-[11px] text-n-slate-11">
          {{ footer.text }}
        </p>
        <p class="mt-1 text-end text-[10px] text-n-slate-10">{{ now }}</p>
      </div>
      <div
        v-if="template && buttons.length"
        class="me-6 mt-1 flex flex-col gap-1"
      >
        <div
          v-for="(b, i) in buttons"
          :key="i"
          class="rounded-lg bg-white py-1.5 text-center text-[13px] font-medium text-[#00a5f4] shadow-sm dark:bg-n-solid-3"
        >
          {{ b.text }}
        </div>
      </div>
    </div>
  </div>
</template>
